/*
 * Brisa variant media filter v3
 *
 * Hides product gallery media that does not match the selected variant.
 *
 * Strategy (in priority order, per media item — see shouldHideItem):
 *   Signal 1: /products/{handle}.js maps media.id → variant_ids[]. A media item
 *             is hidden if it is "owned" by a variant other than the selected
 *             one. Media without variant assignment is universal (always shown).
 *   Signal 2: data-media-alt regex `color:(...)` compared to the selected color
 *             (legacy path; useful where ALTs are tagged but featured_media isn't).
 *   Override: the featured_media of the selected variant is ALWAYS visible.
 *   Signal 3 (caller, aggregate): if the filter would hide ALL items, revert to
 *             "show all" — better a mixed gallery than an empty one (Gina 4324).
 *
 * v3 changes (multi-agent gate 2026-06-05, backlog #20):
 *   - shouldHideItem() is a PURE function (flat descriptor, no DOM) — testable.
 *   - getSelectedColor() is robust to localized/renamed color axes: tries the
 *     named axis (name or data-option-slug), then falls back to cross-referencing
 *     the selected option values against the `color:` tags present in the gallery
 *     ALTs — exact match, bails to null if 0 or >1 match (HIGH-3).
 *   - Re-apply on ANY input inside <variant-picker> (closest), not just inputs
 *     whose name contains 'color'/'option' — fixes no-refilter on Tamaño/Lado (HIGH-2).
 *   - Single MutationObserver on the stable .shopify-section root (the theme does
 *     innerHTML-swap of the gallery's children, verified 2026-06-05) stored on the
 *     node so it can be disconnected; all triggers coalesce into one frame (HIGH-1).
 */
(function () {
  'use strict';

  var HIDE_CLASS = 'brisa-media-hidden';
  var DATA_CACHE = Object.create(null); // handle → Promise<productJson>

  function debug(/* ...args */) {
    if (window.__BRISA_FILTER_DEBUG) {
      // eslint-disable-next-line no-console
      console.log.apply(console, ['[brisa-filter]'].concat([].slice.call(arguments)));
    }
  }

  function getProductHandle() {
    var match = (location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1];
    return match || null;
  }

  function fetchProductData(handle) {
    if (DATA_CACHE[handle]) return DATA_CACHE[handle];
    DATA_CACHE[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return DATA_CACHE[handle];
  }

  function buildMaps(productData) {
    // The DOM uses `data-media-id` = the MediaImage id (NOT the Image id).
    //   variant.featured_media.id → MediaImage id (matches data-media-id) ✓
    //   media[].id                → MediaImage id (matches data-media-id) ✓
    var mediaToVariants = new Map();
    var variantFeaturedIds = new Set();
    var variantToFeatured = new Map();
    if (!productData || !Array.isArray(productData.variants)) {
      return { mediaToVariants: mediaToVariants, variantFeaturedIds: variantFeaturedIds, variantToFeatured: variantToFeatured };
    }
    function record(mediaId, variantId) {
      if (!mediaId || !variantId) return;
      if (!mediaToVariants.has(mediaId)) mediaToVariants.set(mediaId, new Set());
      mediaToVariants.get(mediaId).add(variantId);
    }
    productData.variants.forEach(function (v) {
      var fm = v.featured_media && v.featured_media.id;
      if (fm) {
        variantFeaturedIds.add(fm);
        record(fm, v.id);
        variantToFeatured.set(v.id, fm);
      }
    });
    (productData.media || []).forEach(function (m) {
      if (Array.isArray(m.variant_ids) && m.variant_ids.length) {
        m.variant_ids.forEach(function (vid) { record(m.id, vid); });
      }
    });
    return { mediaToVariants: mediaToVariants, variantFeaturedIds: variantFeaturedIds, variantToFeatured: variantToFeatured };
  }

  function parseColorFromAlt(alt) {
    if (!alt) return null;
    var m = alt.match(/color:([^|]+)/i);
    return m ? m[1].trim() : null;
  }

  function toVariantId(raw) {
    // LOW-1: a malformed value must read as "no selection" (null), never NaN —
    // NaN would never match any owner set and could over-hide (Signal 3 reverts,
    // but be explicit).
    var n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  function getSelectedVariantId() {
    var qs = new URLSearchParams(location.search).get('variant');
    if (qs) return toVariantId(qs);
    var inp = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (inp && inp.value) return toVariantId(inp.value);
    return null;
  }

  function readOptionValue(el) {
    if (!el) return null;
    if (el.tagName === 'SELECT') {
      return el.value || (el.options[el.selectedIndex] && el.options[el.selectedIndex].value) || null;
    }
    // Concept theme prefers data-option-value, fallback to value
    return el.getAttribute('data-option-value') || el.value || null;
  }

  function isColorAxis(el) {
    var name = (el.getAttribute('name') || '').toLowerCase().split('-template')[0];
    if (name.indexOf('color') !== -1 || name.indexOf('colour') !== -1) return true;
    var wrap = el.closest && el.closest('[data-option-slug]');
    var slug = wrap ? (wrap.getAttribute('data-option-slug') || '').toLowerCase() : '';
    return slug.indexOf('color') !== -1 || slug.indexOf('colour') !== -1;
  }

  // HIGH-3: robust color detection. Named axis first; else cross-reference the
  // selected option values against the colors tagged in the gallery ALTs.
  // Exact match only; bails to null on ambiguity (0 or >1 hits) so Signal 1 decides.
  function getSelectedColor(picker, galleryColors) {
    if (!picker) return null;
    var nodes = picker.querySelectorAll('input[type="radio"]:checked, select');
    var selectedValues = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var val = readOptionValue(el);
      if (val) {
        val = val.trim();
        selectedValues.push(val);
        if (isColorAxis(el)) return val; // named color axis wins
      }
    }
    if (galleryColors && galleryColors.size) {
      var matches = selectedValues.filter(function (v) { return galleryColors.has(v); });
      if (matches.length === 1) return matches[0]; // exact, unambiguous
    }
    return null;
  }

  // PURE: no DOM, no side effects. Decides whether ONE media item is hidden.
  // media: { mediaId:number, color:string|null }
  // ctx:   { mediaToVariants:Map, selectedVariantId:number|null,
  //          selectedColor:string|null, featuredForSelected:number|null }
  function shouldHideItem(media, ctx) {
    // Override: the featured_media of the selected variant is ALWAYS visible.
    if (ctx.featuredForSelected && media.mediaId === ctx.featuredForSelected) return false;
    // Signal 1: media assigned to specific variants — hide if not the selected one.
    if (ctx.mediaToVariants && ctx.mediaToVariants.size) {
      var assigned = ctx.mediaToVariants.get(media.mediaId);
      if (assigned && !assigned.has(ctx.selectedVariantId)) return true;
    }
    // Signal 2: ALT tags a color — hide if not the selected color.
    if (media.color && ctx.selectedColor && media.color !== ctx.selectedColor) return true;
    return false;
  }

  // LOW-3 (gate #27): a slider re-init may add/remove nodes → the observer fires
  // once more → one extra processAll. Intentionally NOT guarded with an `inRefresh`
  // flag: that pass is idempotent (re-toggles classes to identical values, ~2 frames)
  // and a sync flag can't reliably bracket the async/microtask-batched observer
  // callback — it would risk swallowing a legitimate concurrent re-render. Cheap and
  // correct beats clever and stale.
  function refreshSlider(gallery) {
    if (!gallery) return;
    var slider = gallery.querySelector('slider-element');
    if (!slider) return;
    if (typeof slider.update === 'function') {
      try { slider.update(); return; } catch (e) {}
    }
    if (typeof slider.initSlider === 'function') {
      try { slider.initSlider(); return; } catch (e) {}
    }
    // Do NOT dispatch a global resize event — too broad.
  }

  function applyFilterCombined(gallery, maps, selectedVariantId, selectedColor) {
    var nodes = gallery.querySelectorAll('.product__media[data-media-id]');
    if (!nodes.length) return { applied: false, hidden: 0, total: 0, mode: 'no-items' };
    var ctx = {
      mediaToVariants: maps && maps.mediaToVariants,
      selectedVariantId: selectedVariantId,
      selectedColor: selectedColor,
      featuredForSelected: maps && maps.variantToFeatured ? maps.variantToFeatured.get(selectedVariantId) : null
    };
    // Pass 1: read DOM → flat descriptors → pure decisions (no DOM writes yet).
    var anyTagged = false;
    var decisions = [];
    nodes.forEach(function (n) {
      var color = parseColorFromAlt(n.getAttribute('data-media-alt'));
      if (color) anyTagged = true;
      var media = { mediaId: Number(n.getAttribute('data-media-id')) || 0, color: color };
      decisions.push({ node: n, hide: shouldHideItem(media, ctx) });
    });
    var hideCount = 0;
    decisions.forEach(function (d) { if (d.hide) hideCount++; });
    // Signal 3 (aggregate): if ALL would hide, show all — no flash, single write.
    var allHidden = hideCount === decisions.length;
    decisions.forEach(function (d) {
      d.node.classList.toggle(HIDE_CLASS, allHidden ? false : d.hide);
    });
    if (allHidden) {
      debug('Signal 3 fallback: all items would be hidden, reverted');
      return { applied: false, hidden: 0, total: nodes.length, mode: 'fallback-all-hidden' };
    }
    var mode = [];
    if (maps && maps.mediaToVariants && maps.mediaToVariants.size) mode.push('variant-id');
    if (anyTagged) mode.push('alt-color');
    return { applied: mode.length > 0, hidden: hideCount, total: nodes.length, mode: mode.join('+') || 'none' };
  }

  function collectGalleryColors(gallery) {
    var colors = new Set();
    gallery.querySelectorAll('.product__media[data-media-alt]').forEach(function (n) {
      var c = parseColorFromAlt(n.getAttribute('data-media-alt'));
      if (c) colors.add(c);
    });
    return colors;
  }

  function processGallery(gallery, maps) {
    if (!gallery) return;
    // LOW-2: anchor on the SAME stable root the observer uses (.shopify-section),
    // not a bare <section>; layered fallback keeps the old behavior if absent.
    var section = gallery.closest('.shopify-section') || gallery.closest('section') || document;
    var picker = section.querySelector('variant-picker') || document.querySelector('variant-picker');
    var variantId = getSelectedVariantId();
    var galleryColors = collectGalleryColors(gallery);
    var color = getSelectedColor(picker, galleryColors);
    var result = applyFilterCombined(gallery, maps, variantId, color);
    debug('gallery filtered', result, 'variantId=' + variantId, 'color=' + color);
    refreshSlider(gallery);
  }

  function processAll(maps) {
    document.querySelectorAll('media-gallery').forEach(function (g) {
      processGallery(g, maps);
    });
  }

  // Coalesce every trigger (change / variant:change / popstate / observer) into a
  // single processAll per frame. Two rAFs: lets the theme update input[name=id] first.
  function makeScheduler(mapsRef) {
    var scheduled = false;
    return function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          scheduled = false;
          processAll(mapsRef.current);
        });
      });
    };
  }

  // HIGH-1: observe the STABLE .shopify-section root (theme does innerHTML-swap of
  // the gallery's children — verified 2026-06-05). One observer, stored on the node
  // so it survives re-bootstrap and can be disconnected. childList-only + the
  // class-only writes mean we never self-trigger; the scheduler debounces bursts.
  function installObserver(root, schedule) {
    if (!root || root._brisaObserver) return;
    var obs = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        return m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length);
      });
      if (relevant) schedule();
    });
    obs.observe(root, { childList: true, subtree: true });
    root._brisaObserver = obs;
  }

  function bootstrap() {
    var handle = getProductHandle();
    var mapsRef = { current: null };
    var schedule = makeScheduler(mapsRef);

    if (handle) {
      fetchProductData(handle).then(function (data) {
        if (data) {
          mapsRef.current = buildMaps(data);
          debug('maps built for', handle, mapsRef.current);
        } else {
          debug('no product data for', handle);
        }
        processAll(mapsRef.current);
      });
    }

    document.querySelectorAll('media-gallery').forEach(function (g) {
      var root = g.closest('.shopify-section') || g.parentElement || document.body;
      installObserver(root, schedule);
    });

    // HIGH-2: re-apply for ANY input inside <variant-picker> (Tamaño/Lado/Material
    // too), not just color/option by name. variant:change is the theme-native net.
    document.addEventListener('variant:change', schedule);
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.closest && t.closest('variant-picker')) schedule();
    });
    window.addEventListener('popstate', schedule);
  }

  // Expose pure internals for unit testing (namespaced, inert in production).
  window.__brisaFilter = {
    shouldHideItem: shouldHideItem,
    parseColorFromAlt: parseColorFromAlt,
    toVariantId: toVariantId
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
